import { z } from 'zod';
import { IsoTimestamp, SchoolId } from './common.js';
import { AcademicMappingEntityKind, AcademicMappingSourceContext } from './academic-mapping-preview.js';
/**
 * AIMSIS academic sync run history (AIMSIS-18809).
 *
 * One persisted entry per preview or apply run over the academic reference and
 * relationship data, plus the per-record detail an administrator reads in the
 * run detail view.
 *
 * The vocabulary is deliberately wider than the preview can currently produce.
 * The run summary the ticket specifies reports totals for created, updated,
 * unchanged, kept, inactivated, archived, skipped, conflicted, failed, and
 * review-required records — including the outcomes only the apply step
 * (AIMSIS-18807) can produce — so the contract is stable when that write path
 * starts recording through it. A preview run reports those buckets as zero
 * rather than omitting them, so a reader never has to guess whether a missing
 * key means "none" or "not applicable".
 *
 * History is append-only and observational: reading it never modifies CBT
 * records, and nothing here re-runs, retries, or resolves a conflict.
 */
/** Which operation produced the run. */
export const AcademicSyncMode = z.enum(['preview', 'apply']);
/**
 * How a run ended. `incomplete` is distinct from `failed`: the run stopped
 * before finishing, so its counts describe a partial pass rather than a
 * finished one that produced errors.
 */
export const AcademicSyncRunStatus = z.enum(['succeeded', 'failed', 'incomplete']);
/**
 * The action taken (or proposed, for a preview) for one record.
 *
 * `kept` is a record left untouched because it is CBT-only — it has no AIMSIS
 * counterpart and is never deleted by a sync. `unchanged` is an integrated
 * record a sync compared and found already correct.
 */
export const AcademicSyncRecordOutcome = z.enum([
    'created',
    'updated',
    'unchanged',
    'kept',
    'inactivated',
    'archived',
    'skipped',
    'conflicted',
    'failed',
    'reviewRequired',
]);
/**
 * Whether a source record was matched to a CBT record. Reconciliation is
 * reported, never acted on — no value here triggers a write or a retry.
 *
 *   matched    — a source row and its CBT counterpart were identified
 *   sourceOnly — the source row has no CBT record yet
 *   cbtOnly    — an integrated CBT record has no source row in this window
 */
export const AcademicSyncReconciliation = z.enum(['matched', 'sourceOnly', 'cbtOnly']);
/**
 * Per-dataset totals. Every bucket is always present, so a consumer can sum or
 * compare them without treating a missing key as zero.
 */
export const AcademicSyncRunCounts = z.object({
    fetched: z.number().int().nonnegative(),
    matched: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    unchanged: z.number().int().nonnegative(),
    kept: z.number().int().nonnegative(),
    inactivated: z.number().int().nonnegative(),
    archived: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    conflicted: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    reviewRequired: z.number().int().nonnegative(),
});
/**
 * One row of the run detail view.
 *
 * `externalId` and `cbtId` are both nullable because a record can exist on
 * either side alone; `reconciliation` states which case it is rather than
 * leaving a reader to infer it from nulls.
 */
export const AcademicSyncRunRecord = z.object({
    dataset: AcademicMappingEntityKind,
    /** AIMSIS identity used for matching, when the source row has one. */
    externalId: z.string().nullable(),
    /** Internal CBT document id, when a counterpart was identified. */
    cbtId: z.string().nullable(),
    /** Readable label, or a relationship summary for the relationship types. */
    name: z.string().nullable(),
    reconciliation: AcademicSyncReconciliation,
    outcome: AcademicSyncRecordOutcome,
    /** Why the record landed in this outcome — required for the exception rows. */
    reason: z.string().nullable(),
    /** Error detail for a `failed` record. */
    errorMessage: z.string().nullable(),
    /** Relationship window, for Class Memberships and Teaching Assignments. */
    effectiveFrom: z.string().nullable(),
    effectiveTo: z.string().nullable(),
});
/** Per-dataset roll-up within a run. */
export const AcademicSyncDatasetSummary = z.object({
    dataset: AcademicMappingEntityKind,
    /** False when the source of this data type could not be read at all. */
    available: z.boolean(),
    counts: AcademicSyncRunCounts,
    unavailableReason: z.string().nullable(),
});
/** Who initiated the run. */
export const AcademicSyncActor = z.object({
    uid: z.string().min(1),
    /** CBT display name when resolvable, else the uid. */
    displayName: z.string().min(1),
});
/**
 * One persisted run. Carries the summary only — the per-record detail lives
 * behind the detail endpoint, so listing history stays cheap regardless of how
 * many records a run touched.
 */
export const AcademicSyncRun = z.object({
    id: z.string().min(1),
    schoolId: SchoolId,
    /** Identifies this as academic sync history, alongside the roster runs. */
    syncType: z.literal('academic'),
    mode: AcademicSyncMode,
    /**
     * Whether the run changed CBT records. Always false for a preview; false for
     * an apply that failed before committing anything.
     */
    written: z.boolean(),
    status: AcademicSyncRunStatus,
    startedAt: IsoTimestamp,
    completedAt: IsoTimestamp,
    durationMs: z.number().int().nonnegative(),
    actor: AcademicSyncActor,
    /** The AIMSIS source this run read, or null in standalone mode. */
    source: AcademicMappingSourceContext.nullable(),
    datasets: z.array(AcademicSyncDatasetSummary),
    /** Sum of `datasets` counts — the run-level figure the summary shows. */
    totals: AcademicSyncRunCounts,
    warnings: z.array(z.string()),
    /** Failure detail when `status` is not `succeeded`. */
    errorMessage: z.string().nullable(),
    /** Records stored for the detail view, and how many the run actually saw. */
    recordsRetained: z.number().int().nonnegative(),
    recordsTotal: z.number().int().nonnegative(),
    /**
     * True when the run produced more records than the retention ceiling, so the
     * detail view is a subset. Surfaced rather than presenting a partial list as
     * complete.
     */
    recordsTruncated: z.boolean(),
});
/** Response of the run history endpoint. */
export const AcademicSyncRunList = z.object({
    /** False in standalone mode, where no AIMSIS source is configured. */
    configured: z.boolean(),
    runs: z.array(AcademicSyncRun),
    /**
     * True when the tenant has more runs than the page returned. Surfaced so a
     * capped list is never read as the complete history.
     */
    hasMore: z.boolean(),
});
/** Response of the run detail endpoint. */
export const AcademicSyncRunDetail = z.object({
    run: AcademicSyncRun,
    records: z.array(AcademicSyncRunRecord),
});
//# sourceMappingURL=academic-sync-run.js.map